import { CustomTabs, PageHeader, ErrorBoundary } from '@circuitry-ai/doc-ui';
import { useState, useEffect, Key } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import FormGenieConfiguration from './FormGenieConfiguration';
import FormGenieHistory from './FormGenieHistory';

export interface FormGenieViewProps {
  basePath?: string;
}

export enum FormGenieViewTabs {
  CONFIGURATION = 'configuration',
  FORMS = 'forms',
}

// Hoist ErrorBoundary-wrapped components to module scope
const FormGenieConfigurationWithErrorBoundary = ErrorBoundary(
  FormGenieConfiguration as unknown as React.ComponentType<Record<string, unknown>>
);
const FormGenieHistoryWithErrorBoundary = ErrorBoundary(
  FormGenieHistory as unknown as React.ComponentType<Record<string, unknown>>
);

export function FormGenieView({ basePath = '/formgenie' }: FormGenieViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const tab = queryParams.get('tab');
  const { mode } = useParams<{ mode: string }>();

  const { formGenieWorker } = (location?.state ?? {}) as any;
  const aiworkerId = formGenieWorker?.aiworker_id || (mode !== 'create' ? mode : undefined);
  const isCreateMode = mode === 'create';

  const { canUpdate } = useEntityResourcePermissions('uxgenie', isCreateMode ? undefined : aiworkerId);

  const disabledKeys = isCreateMode
    ? [FormGenieViewTabs.FORMS]
    : !canUpdate
    ? [FormGenieViewTabs.CONFIGURATION]
    : [];

  const tabItems = [
    { key: FormGenieViewTabs.CONFIGURATION, title: 'Configuration', id: 'configuration-tab' },
    { key: FormGenieViewTabs.FORMS, title: 'Forms', id: 'forms-tab' },
  ];

  const [currentTab, setCurrentTab] = useState<string>(tab ?? FormGenieViewTabs.CONFIGURATION);

  useEffect(() => {
    setCurrentTab(tab ?? FormGenieViewTabs.CONFIGURATION);
  }, [tab]);

  useEffect(() => {
    if (disabledKeys.includes(currentTab as FormGenieViewTabs)) {
      setCurrentTab(isCreateMode ? FormGenieViewTabs.CONFIGURATION : FormGenieViewTabs.FORMS);
    }
  }, [disabledKeys, currentTab, isCreateMode]);

  const onTabChange = (key: Key) => {
    setCurrentTab(key as string);
    navigate(`${basePath}/${mode}?tab=${key}`, { state: { formGenieWorker } });
  };

  const renderSelectedTab = () => {
    switch (currentTab) {
      case FormGenieViewTabs.CONFIGURATION:
        return <FormGenieConfigurationWithErrorBoundary basePath={basePath} />;
      case FormGenieViewTabs.FORMS:
        return <FormGenieHistoryWithErrorBoundary aiworkerId={aiworkerId} basePath={basePath} />;
      default:
        return <FormGenieConfigurationWithErrorBoundary basePath={basePath} />;
    }
  };

  return (
    <div className="flex flex-col h-full gap-2">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 pt-4 pb-2 text-sm">
        <button
          onClick={() => navigate(basePath)}
          className="text-default-500 hover:text-blue-600 transition-colors"
        >
          FormGenie Workers
        </button>
        <Icon icon="mdi:chevron-right" width={16} className="text-default-400 flex-shrink-0" />
        <span className="text-foreground font-medium">
          {formGenieWorker?.name || 'Worker Configuration'}
        </span>
      </nav>

      <div className="advisor-tab-list">
        <CustomTabs
          tabs={tabItems}
          selectedKey={currentTab}
          onTabChange={onTabChange}
          disabledKeys={disabledKeys}
        />
      </div>
      <div className="h-[78vh] overflow-y-hidden">{renderSelectedTab()}</div>
    </div>
  );
}

export default FormGenieView;
